import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { Footer, SEO } from '@/components';
import { track } from '@/lib/analytics';
import {
  COMPARISON_FEATURES,
  getCompetitorBySlug,
  isCompetitorSlug,
} from '@/lib/competitors';
import { getAppStoreUrl, getPlayStoreUrl, getUtmParamsFromUrl } from '@/lib/urls';
import shipSvg from '@/assets/ship.svg';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

function asCell(value: boolean): string {
  return value ? 'Yes' : 'No';
}

function UnknownCompetitorPage({ competitorSlug }: { competitorSlug: string }) {
  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Comparison not found | Harbor'
        description='This comparison page is not available.'
        path={`/compare/${competitorSlug}`}
        noindex
      />
      <main className='mx-auto max-w-3xl px-6 py-24 text-center'>
        <h1 className='text-3xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
          Comparison not found
        </h1>
        <p className='mt-4 text-lg text-gray-600' style={nunito}>
          This competitor page is not available. Browse the full comparison list instead.
        </p>
        <Link
          to='/compare'
          className='mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white hover:bg-primary-hover'
          style={{ ...nunito, fontWeight: 600 }}
        >
          <FiArrowLeft />
          Back to all comparisons
        </Link>
      </main>
      <Footer />
    </div>
  );
}

function CompetitorPage() {
  const { competitor: competitorSlug } = Route.useParams();

  if (!isCompetitorSlug(competitorSlug)) {
    return <UnknownCompetitorPage competitorSlug={competitorSlug} />;
  }

  const competitor = getCompetitorBySlug(competitorSlug);
  const path = `/compare/${competitor.slug}`;

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: competitor.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://harborcoparent.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Compare',
        item: 'https://harborcoparent.com/compare',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `Harbor vs ${competitor.name}`,
        item: `https://harborcoparent.com/compare/${competitor.slug}`,
      },
    ],
  };

  const harborSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Harbor',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'iOS, Android, Web',
    offers: {
      '@type': 'Offer',
      price: '9.99',
      priceCurrency: 'USD',
      description: 'Monthly plan',
    },
    url: 'https://harborcoparent.com',
  };

  const competitorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: competitor.name,
    description: competitor.tagline,
    url: competitor.pricingSourceUrl,
  };

  useEffect(() => {
    track('Compare Competitor View', { competitor: competitor.slug });
  }, [competitor.slug]);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title={`Harbor vs ${competitor.name}: Pricing, Features & Best Co-Parenting App in 2026`}
        description={`Compare Harbor vs ${competitor.name} on pricing, core features, and day-to-day co-parent workflows. See why Harbor is often the better value for most families.`}
        path={path}
        jsonLd={[faqJsonLd, breadcrumbJsonLd, harborSchema, competitorSchema]}
      />

      <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
        <nav className='mx-auto flex max-w-7xl items-center justify-between px-6 py-4'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-2xl font-light tracking-tight text-white'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <img src={shipSvg} alt='' className='mb-1 mr-2 h-5 w-5' />
            <span>Harbor</span>
          </Link>
          <Link
            to='/compare'
            className='flex items-center gap-2 font-medium text-white/90 transition-colors hover:text-white'
            style={{ ...nunito, fontWeight: 400 }}
          >
            <FiArrowLeft />
            Back to Compare
          </Link>
        </nav>
      </header>

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-5xl px-6'>
          <h1 className='text-4xl font-bold text-gray-900 md:text-5xl' style={{ ...nunito, fontWeight: 700 }}>
            Harbor vs {competitor.name}
          </h1>
          <p className='mt-5 max-w-4xl text-lg leading-relaxed text-gray-700 md:text-xl' style={nunito}>
            {competitor.vsHarbor}
          </p>
          <p className='mt-4 max-w-4xl text-base text-gray-800' style={nunito}>
            <strong className='font-semibold'>Our take:</strong> If you want one app for daily
            co-parenting logistics with strong value versus higher-priced options, Harbor is usually the better first choice.
            Consider {competitor.name} when your needs match its specific strengths listed below.
          </p>
          <p className='mt-4 text-sm text-gray-500' style={nunito}>
            Pricing source:{" "}
            <a
              href={competitor.pricingSourceUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='font-semibold text-primary underline'
            >
              {competitor.name} pricing
            </a>
          </p>
        </div>
      </section>

      <section className='mx-auto grid max-w-6xl gap-6 px-6 py-12 md:grid-cols-2'>
        <article className='rounded-xl border border-emerald-200 bg-emerald-50/40 p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            Harbor at a glance
          </h2>
          <ul className='mt-4 space-y-2 text-gray-700' style={nunito}>
            <li>$9.99/month or $49.99/year</li>
            <li>Messaging, expenses, custody calendar, exports, and file vault in one app</li>
            <li>Designed for simple daily workflows and clear records</li>
          </ul>
        </article>
        <article className='rounded-xl border border-gray-200 bg-white p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            {competitor.name} at a glance
          </h2>
          <ul className='mt-4 space-y-2 text-gray-700' style={nunito}>
            <li>{competitor.pricingMonthly}</li>
            <li>{competitor.pricingAnnual}</li>
            <li>{competitor.tagline}</li>
          </ul>
        </article>
      </section>

      <section className='mx-auto max-w-6xl px-6 pb-12'>
        <h2 className='mb-5 text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
          Feature comparison
        </h2>
        <div className='overflow-x-auto rounded-2xl border border-gray-200'>
          <table className='min-w-full border-collapse bg-white text-left' style={nunito}>
            <caption className='sr-only'>Harbor and competitor feature matrix</caption>
            <thead className='bg-gray-50'>
              <tr>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Feature
                </th>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-primary'>
                  Harbor
                </th>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  {competitor.name}
                </th>
              </tr>
            </thead>
            <tbody className='text-sm text-gray-700'>
              <tr>
                <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Editorial recommendation
                </th>
                <td className='border-b border-r border-gray-200 p-4 font-semibold text-primary'>
                  Recommended for most families
                </td>
                <td className='border-b border-r border-gray-200 p-4'>
                  Consider for specific priorities
                </td>
              </tr>
              <tr>
                <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Monthly pricing
                </th>
                <td className='border-b border-r border-gray-200 p-4'>$9.99 / month</td>
                <td className='border-b border-r border-gray-200 p-4'>{competitor.pricingMonthly}</td>
              </tr>
              <tr>
                <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Annual pricing
                </th>
                <td className='border-b border-r border-gray-200 p-4'>$49.99 / year</td>
                <td className='border-b border-r border-gray-200 p-4'>{competitor.pricingAnnual}</td>
              </tr>
              {COMPARISON_FEATURES.map((feature) => (
                <tr key={feature.key}>
                  <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                    {feature.label}
                  </th>
                  <td className='border-b border-r border-gray-200 p-4'>
                    {asCell(feature.harborIncluded)}
                  </td>
                  <td className='border-b border-r border-gray-200 p-4'>
                    {asCell(competitor.featureMatrix[feature.key])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className='mx-auto grid max-w-6xl gap-6 px-6 pb-12 md:grid-cols-2'>
        <article className='rounded-xl border border-gray-200 bg-white p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            What {competitor.name} does well
          </h2>
          <ul className='mt-4 list-disc space-y-2 pl-5 text-gray-700' style={nunito}>
            {competitor.pros.map((pro) => (
              <li key={pro}>{pro}</li>
            ))}
          </ul>
        </article>
        <article className='rounded-xl border border-gray-200 bg-white p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            Where Harbor is usually stronger
          </h2>
          <ul className='mt-4 list-disc space-y-2 pl-5 text-gray-700' style={nunito}>
            {competitor.cons.map((con) => (
              <li key={con}>{con}</li>
            ))}
          </ul>
        </article>
      </section>

      <section className='mx-auto max-w-6xl px-6 pb-12'>
        <div className='rounded-xl border border-blue-200 bg-blue-50/40 p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            Recommendation
          </h2>
          <p className='mt-3 text-gray-700' style={nunito}>
            <strong className='font-semibold'>Best for {competitor.name}:</strong> {competitor.bestFor}
          </p>
          <p className='mt-3 text-gray-700' style={nunito}>
            <strong className='font-semibold'>Harbor verdict:</strong> {competitor.verdict}
          </p>
          <p className='mt-3 text-gray-700' style={nunito}>
            <strong className='font-semibold'>Bottom line:</strong> Start with Harbor unless you have a
            clear, specific reason to prioritize {competitor.name}.
          </p>
        </div>
      </section>

      <section className='mx-auto max-w-6xl px-6 pb-12'>
        <h2 className='mb-5 text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
          Frequently asked questions
        </h2>
        <div className='space-y-4'>
          {competitor.faqs.map((faq) => (
            <article key={faq.question} className='rounded-xl border border-gray-200 bg-white p-5'>
              <h3 className='text-lg font-semibold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                {faq.question}
              </h3>
              <p className='mt-2 text-gray-700' style={nunito}>
                {faq.answer}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className='bg-primary py-14'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h2 className='text-3xl font-bold text-white' style={{ ...nunito, fontWeight: 700 }}>
            Start with Harbor first
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-white/85' style={nunito}>
            Test Harbor in your real co-parent workflow before committing to a higher-cost alternative.
          </p>
          <div className='mt-8 flex flex-col justify-center gap-3 sm:flex-row'>
            <a
              href={getAppStoreUrl(`compare-${competitor.slug}`, getUtmParamsFromUrl())}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
              style={{ ...nunito, fontWeight: 600 }}
            >
              <FaApple size={18} />
              App Store
            </a>
            <a
              href={getPlayStoreUrl(`compare-${competitor.slug}`, getUtmParamsFromUrl())}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
              style={{ ...nunito, fontWeight: 600 }}
            >
              <FaGooglePlay size={18} />
              Google Play
            </a>
            <Link
              to='/auth/sign-up'
              className='inline-flex items-center justify-center rounded-lg border border-white px-5 py-3 font-semibold text-white hover:bg-white/10'
              style={{ ...nunito, fontWeight: 600 }}
            >
              Sign up on web
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/compare/$competitor')({
  component: CompetitorPage,
});
